import React, { useState } from 'react';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    { type: 'success' | 'error'; message: string } | null
  >(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();

    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d3bh78hec2kf3t4m0g60',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: data.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message:
            'Thank you for your message! We will contact you soon to discuss your art installation project.',
        });
        form.reset();
      } else {
        const errorText = await response.text();
        throw new Error(
          errorText || 'Failed to submit form: server returned an error.'
        );
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message:
          'Sorry, there was an error sending your message. Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="mr-4">
                <img
                  src="https://static.readdy.ai/image/87505c603ef0feae3c12190971600621/14ab2511f78ce986f6e72d71f1d662e9.png"
                  alt="Art Installation Excellence Logo"
                  className="h-16 w-auto"
                />
              </div>
              <h1
                className="text-2xl font-bold text-yellow-600"
                style={{ fontFamily: 'Lucida Calligraphy, cursive' }}
              >
                Art Installation Excellence
              </h1>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-800 hover:text-yellow-600 transition-colors cursor-pointer">
                Services
              </a>
              <a href="#gallery" className="text-gray-800 hover:text-yellow-600 transition-colors cursor-pointer">
                Gallery
              </a>
              <a href="#about" className="text-gray-800 hover:text-yellow-600 transition-colors cursor-pointer">
                About
              </a>
              <a href="#contact" className="text-gray-800 hover:text-yellow-600 transition-colors cursor-pointer">
                Contact
              </a>
              <button className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors cursor-pointer whitespace-nowrap font-semibold">
                Get Quote
              </button>
            </div>
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-800 hover:text-yellow-600 cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <a href="#services" className="block text-gray-800 hover:text-yellow-600 cursor-pointer">
                Services
              </a>
              <a href="#gallery" className="block text-gray-800 hover:text-yellow-600 cursor-pointer">
                Gallery
              </a>
              <a href="#about" className="block text-gray-800 hover:text-yellow-600 cursor-pointer">
                About
              </a>
              <a href="#contact" className="block text-gray-800 hover:text-yellow-600 cursor-pointer">
                Contact
              </a>
              <button className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors cursor-pointer whitespace-nowrap font-semibold">
                Get Quote
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="relative h-screen flex items-start justify-center bg-cover bg-center pt-32"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9)), url('https://public.readdy.ai/ai/img_res/3a9167ec9d714105f0cf1ac258dce951.jpg')`,
        }}
      >
        <div className="text-center max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-normal mb-6 leading-tight text-yellow-600" style={{ fontFamily: 'Lucida Calligraphy, cursive', fontWeight: '400' }}>
            Professional Art Installation & Exhibition Services
          </h1>
        </div>
      </section>

      {/* About Section - Moved here */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">About Art Installation Excellence</h2>
              <p className="text-lg text-gray-600 mb-6">
                Art Installation Excellence was born from years of experience at MGM RELO LTD.
                What began within the relocation industry has grown into a dedicated pursuit of perfection in fine art handling and installation.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our reputation has been shaped through collaborations with prestigious companies such as Cadogan Tate, Christie's, The British Shop, Total Fine Art Solutions, and others — delivering precision, discretion, and care on high-profile projects across London and beyond.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We primarily focus on the private sector, while continuously expanding our work with galleries, museums, and exhibition centres — wherever professional and precise art installation is required.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our team of experienced art handlers and installation specialists maintains the highest standards in every project, from intimate private collections to major exhibitions.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our team of experienced art handlers and installation specialists maintains the highest standards in every project, from intimate private collections to major exhibitions.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_22d4d505132e6c9e0279d4c029162fc1_eba1e743.jpg"
                alt="AIX Team installing artwork in gallery"
                className="rounded-xl shadow-lg w-full h-96 object-cover object-top border border-gray-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Art Installation Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive art installation services with the highest level of professionalism and expertise across various specialized areas.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Private Collections - Left Side */}
            <div className="relative">
              <div className="bg-white border border-gray-200 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <div className="flex items-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center mr-6">
                    <i className="ri-home-heart-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Private Collections</h3>
                </div>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Complete residential and corporate art installation services, including specialized hanging solutions for all your decorative needs.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover-bg-yellow-50 transition-colors border border-gray-100">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-yellow-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Artwork Installation</h4>
                      <p className="text-gray-600 text-sm">Professional hanging and positioning of paintings, prints, and photographs</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover-bg-yellow-50 transition-colors border border-gray-100">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-mirror-line text-yellow-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Mirror Hanging</h4>
                      <p className="text-gray-600 text-sm">Secure installation of decorative and functional mirrors with precision mounting</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover-bg-yellow-50 transition-colors border border-gray-100">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-window-line text-yellow-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Curtains Installation</h4>
                      <p className="text-gray-600 text-sm">Expert curtain and drapery hanging for residential and commercial spaces</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover-bg-yellow-50 transition-colors border border-gray-100">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-lightbulb-line text-yellow-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Chandelier Installation</h4>
                      <p className="text-gray-600 text-sm">Specialized hanging of luxury lighting fixtures with electrical safety</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Installations - Right Side */}
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-900 to-black p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-white h-full">
                <div className="flex items-center mb-8">
                  <div className="w-20 h-20 bg-yellow-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-6 border border-yellow-600/30">
                    <i className="ri-building-line text-3xl text-yellow-400"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-yellow-400">Professional Installations</h3>
                </div>
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  Museum-standard installations for galleries, exhibitions, and cultural institutions with comprehensive logistics support.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl hover-bg-white/20 transition-colors border border-white/10">
                    <div className="w-12 h-12 bg-yellow-600/20 rounded-lg flex items-center justify-center mb-4 border border-yellow-600/30">
                      <i className="ri-truck-line text-xl text-yellow-400"></i>
                    </div>
                    <h4 className="font-semibold mb-2 text-yellow-400">Art Logistics</h4>
                    <p className="text-gray-300 text-sm">Secure transportation and exhibition logistics coordination</p>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl hover-bg-white/20 transition-colors border border-white/10">
                    <div className="w-12 h-12 bg-yellow-600/20 rounded-lg flex items-center justify-center mb-4 border border-yellow-600/30">
                      <i className="ri-gallery-line text-xl text-yellow-400"></i>
                    </div>
                    <h4 className="font-semibold mb-2 text-yellow-400">Gallery Services</h4>
                    <p className="text-gray-300 text-sm">Professional artwork hanging and exhibition design</p>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl hover-bg-white/20 transition-colors sm:col-span-2 border border-white/10">
                    <div className="w-12 h-12 bg-yellow-600/20 rounded-lg flex items-center justify-center mb-4 border border-yellow-600/30">
                      <i className="ri-ancient-gate-line text-xl text-yellow-400"></i>
                    </div>
                    <h4 className="font-semibold mb-2 text-yellow-400">Museum Installations</h4>
                    <p className="text-gray-300 text-sm">Specialized museum-standard installations with conservation handling and permanent collection setup</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Fully Insured</h4>
              <p className="text-gray-600">Complete insurance coverage for all installations and art handling</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Certified Professionals</h4>
              <p className="text-gray-600">Museum-trained specialists with years of experience</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">24/7 Support</h4>
              <p className="text-gray-600">Round-the-clock assistance for urgent installations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Expertise</h2>
            <p className="text-xl text-gray-600">Professional art installation services we provide</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
              <img
                src="https://readdy.ai/api/search-image?query=Art%20installation%20service%20concept%20with%20white%20gloves%20holding%20picture%20frame%2C%20professional%20art%20handling%20techniques%2C%20careful%20artwork%20positioning%2C%20white%20and%20gold%20color%20scheme%2C%20clean%20professional%20presentation%20of%20art%20handling%20services&width=400&height=300&seq=handling1-white&orientation=landscape"
                alt="Art Handling Services"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Expert Handling</h3>
                <p className="text-gray-600 text-sm">Careful and professional artwork handling</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
              <img
                src="https://readdy.ai/api/search-image?query=Luxury%20chandelier%20and%20lighting%20fixture%20installation%20concept%2C%20elegant%20crystal%20chandelier%20on%20white%20background%20with%20golden%20accents%2C%20professional%20lighting%20installation%20service%2C%20sophisticated%20home%20decoration%20elements&width=400&height=300&seq=lighting1-white&orientation=landscape"
                alt="Lighting Installation"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Lighting Solutions</h3>
                <p className="text-gray-600 text-sm">Professional chandelier and fixture installation</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20gallery%20exhibition%20installation%20concept%2C%20white%20gallery%20walls%20with%20artwork%20being%20installed%2C%20museum-quality%20hanging%20systems%2C%20professional%20exhibition%20setup%20with%20white%20walls%20and%20golden%20frames%2C%20gallery%20lighting%20and%20display%20preparation%2C%20sophisticated%20exhibition%20design%20process&width=400&height=300&seq=exhibitions1-white&orientation=landscape"
                alt="Exhibitions and Galleries"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Exhibitions &amp; Galleries</h3>
                <p className="text-gray-600 text-sm">Professional exhibition and gallery installation services</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
              <img
                src="https://readdy.ai/api/search-image?query=Mirror%20and%20home%20decoration%20installation%20concept%2C%20elegant%20decorative%20mirrors%20and%20curtain%20rods%20on%20white%20background%2C%20professional%20home%20decoration%20installation%20services%2C%20white%20and%20gold%20interior%20design%20elements&width=400&height=300&seq=decoration1-white&orientation=landscape"
                alt="Home Decoration"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Home Decoration</h3>
                <p className="text-gray-600 text-sm">Mirror and curtain installation services</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20consultation%20and%20planning%20concept%2C%20measuring%20tape%20and%20planning%20documents%20on%20white%20background%2C%20art%20installation%20planning%20and%20consultation%20services%2C%20professional%20approach%20to%20artwork%20placement%20with%20golden%20accents&width=400&height=300&seq=consultation1-white&orientation=landscape"
                alt="Professional Consultation"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Expert Consultation</h3>
                <p className="text-gray-600 text-sm">Professional planning and consultation services</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/87505c603ef0feae3c12190971600621/ef6e1657b4b4bb99a2c99587db92f5d4.jpeg"
                  alt="Partnership with MGM RELO LTD"
                  className="w-full h-48 object-cover object-top"
                />
                <div className="absolute inset-0 bg-white/80"></div>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Partnership with MGM RELO LTD</h3>
                <p className="text-gray-600 text-sm">Comprehensive relocation and storage company</p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">Ready to discuss your art installation project?</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                  <p className="text-gray-600">07926 769770</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <p className="text-gray-600">info@aixlondon.co.uk</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                  <p className="text-gray-600">
                    169 Great Portland Street<br />
                    5th Floor<br />
                    London W1W 5PF<br />
                    United Kingdom
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                <h3 className="font-semibold text-gray-900 mb-2">Partnership Services</h3>
                <p className="text-gray-600 mb-3">
                  For comprehensive relocation services, contact our partner:
                </p>
                <a
                  href="https://www.mgmreloltd.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-yellow-600 font-semibold hover:text-yellow-7 cursor-pointer"
                >
                  MGM RELO LTD
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
              <form className="space-y-6" data-readdy-form onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-90  rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Project Type</label>
                  <select
                    name="project_type"
                    className="w-full px-4 py-3 pr-8 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">Select project type</option>
                    <option value="gallery">Gallery Installation</option>
                    <option value="museum">Museum Services</option>
                    <option value="private">Private Collection</option>
                    <option value="exhibition">Exhibition Setup</option>
                    <option value="logistics">Art Logistics</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                    placeholder="Tell us about your project requirements..."
                    maxLength={500}
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-yellow-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-yellow-7 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Logo na środku stopki - poprawione na właściwe logo */}
          <div className="flex justify-center mb-12">
            <img 
              src="https://static.readdy.ai/image/87505c603ef0feae3c12190971600621/14ab2511f78ce986f6e72d71f1d662e9.png" 
              alt="Art Installation Excellence Logo" 
              className="h-20 w-auto"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  07926 769770
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@aixlondon.co.uk
                </p>
                <p className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  169 Great Portland Street, 5th Floor, London W1W 5PF
                </p>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-yellow-400" style={{ fontFamily: 'Lucida Calligraphy, cursive' }}>
                Art Installation Excellence
              </h3>
              <div className="mt-6">
                <h4 className="font-semibold mb-4 text-yellow-400">Partnership</h4>
                <a
                  href="https://www.mgmreloltd.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-yellow-600 font-semibold hover:text-yellow-400 cursor-pointer"
                >
                  MGM RELO LTD
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
            <div className="text-center">
              <h4 className="font-semibold mb-4 text-yellow-400">Navigation</h4>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <a href="#contact" className="hover:text-yellow-400 cursor-pointer">
                    Contact Us
                  </a>
                </li>
                <li>
                  <a href="#about" className="hover:text-yellow-400 cursor-pointer">
                    About
                  </a>
                </li>
                <li>
                  <a href="#gallery" className="hover:text-yellow-400 cursor-pointer">
                    Portfolio
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Art Installation Excellence. All rights reserved.
            </p>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 text-sm hover:text-yellow-400 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
